import React, { useState, useRef } from 'react'
import AudioVisualizer from './AudioVisualizer'

/**
 * Demo component showcasing all AudioVisualizer modes
 * This is for testing and demonstration purposes
 */
const AudioVisualizerDemo = () => {
  const [visualizationMode, setVisualizationMode] = useState('bars')
  const [isMuted, setIsMuted] = useState(false)
  const audioStreamRef = useRef(null)

  const visualizationModes = [
    { key: 'bars', label: 'LED Bars (Original)' },
    { key: 'circular', label: 'Circular' },
    { key: 'knightRider', label: 'Knight Rider Scanner' },
    { key: 'particles', label: 'Particle System' },
    { key: 'bezier', label: 'Bezier Waveform' },
    { key: 'radial', label: 'Radial Spectrogram' },
    { key: 'energy', label: 'Energy Field' },
    { key: 'tunnel', label: '3D Tunnel' },
    { key: 'grid', label: 'Neon Grid' },
    { key: 'fire', label: 'Fire Effect' },
  ]

  const startAudioStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      audioStreamRef.current = stream
    } catch (error) {
      console.error('Error accessing microphone:', error)
    }
  }

  const stopAudioStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop())
      audioStreamRef.current = null
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-6 text-center text-3xl font-bold">
        AudioVisualizer Demo
      </h1>

      <div className="mb-6 flex justify-center gap-4">
        <button
          onClick={startAudioStream}
          className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
        >
          Start Microphone
        </button>
        <button
          onClick={stopAudioStream}
          className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
        >
          Stop Microphone
        </button>
        <button
          onClick={() => setIsMuted(!isMuted)}
          className={`rounded px-4 py-2 ${
            isMuted
              ? 'bg-yellow-500 hover:bg-yellow-600'
              : 'bg-blue-500 hover:bg-blue-600'
          } text-white`}
        >
          {isMuted ? 'Unmute' : 'Mute'}
        </button>
      </div>

      <div className="mb-6">
        <h2 className="mb-3 text-xl font-semibold">Visualization Modes:</h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
          {visualizationModes.map((mode) => (
            <button
              key={mode.key}
              onClick={() => setVisualizationMode(mode.key)}
              className={`rounded px-3 py-2 text-sm ${
                visualizationMode === mode.key
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        <div className="rounded-lg border-2 border-gray-300 bg-black p-4">
          <AudioVisualizer
            audioStream={audioStreamRef.current}
            isMuted={isMuted}
            visualizationMode={visualizationMode}
            width={400}
            height={300}
            className="mx-auto block"
          />
        </div>
      </div>

      <div className="mt-6 text-center text-gray-600">
        <p>
          Current mode:{' '}
          <strong>
            {visualizationModes.find((m) => m.key === visualizationMode)?.label}
          </strong>
        </p>
        <p className="mt-2 text-sm">
          Click &quot;Start Microphone&quot; to begin audio visualization. Try
          different modes to see various effects!
        </p>
      </div>
    </div>
  )
}

export default AudioVisualizerDemo
